import { Link } from '@remix-run/react';
import { FC } from 'react';
import { Image } from '~/components/image';
import { DivisibleStack } from '~/components/divisible-stack';
import { useNumFormatter } from '~/utils/format/num-formatter';
import { useTranslation } from 'react-i18next';
import { LegacyUserUtil, User } from 'shared/models/legacy';
import { ClickPosition } from '~/utils/click-position';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { useSEOHref } from '~/utils/build-seo-url';

import './style.scss';

interface UserCardProps {
  user: User;
}

export const UserCard: FC<UserCardProps> = ({ user }) => {
  const formatNum = useNumFormatter();
  const { t } = useTranslation();
  const active = usePageActive();

  const { description, fansCount, publishCount, nickName, avatar, userId } = user;

  const userHref = useSEOHref(LegacyUserUtil.getPath(user));

  const handleUserClick = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: ClickPosition.related_user,
        page_click_id: userId,
      }),
      reflowBehavior: () => {
        active({
          schemaOption: {
            pageType: 'user',
            userId,
          },
          ampExtra: {
            enter_position: ClickPosition.related_user,
          },
        });
      },
    },
    [active, userId],
  );

  return (
    <Link className="user-recommend-card" to={userHref} onClick={handleUserClick}>
      <div className="user-recommend-card-content">
        <Image
          className="user-recommend-card-content-avatar"
          src={avatar}
          alt={t('own_picture').replace('%s', nickName)}
        />
        <span className="user-recommend-card-content-name">{nickName}</span>
        <p className="user-recommend-card-content-desc">{description}</p>
      </div>
      <DivisibleStack wrapper="user-recommend-card-data" divider="user-recommend-card-data-divider">
        <div className="user-recommend-card-data-item">
          <span>{formatNum(Number(fansCount) || 0)}</span>
          <span>{t('fans_count_number')}</span>
        </div>
        <div className="user-recommend-card-data-item">
          <span>{formatNum(publishCount) || 0}</span>
          <span>{t('wap_seo_post_cnt')}</span>
        </div>
      </DivisibleStack>
    </Link>
  );
};
